import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react'

import { AnonymizationClient } from '../anonymization/client'
import { EnginePool, defaultEngines, maxEngines } from '../anonymization/pool'
import { useApi } from '../lib/api'
import { normalizeToken } from '../lib/keyId'
import { useSession } from '../lib/session'
import { sessionEnded } from '../lib/sessionEnd'
import { readStored, writeStored } from '../lib/stored'
import { useSiteToken } from '../lib/token'
import type { Label } from '../lib/types'
import { WORKING, progressOf } from './job'
import { UploadJobs, leadingJob, type UploadsState } from './jobs'

/** The number of engines the user chose in this browser. */
const ENGINES_KEY = 'pedfx.engines'

const UploadsContext = createContext<UploadJobs | null>(null)

/**
 * The upload jobs of this tab for every signed-in page (`upload/jobs.ts`), so they keep running
 * while the user moves between pages. While one works, the token is locked, the browser asks before
 * the tab closes, and the tab's title shows the progress. A Web Lock held meanwhile is one of the
 * conditions under which Chromium's energy saver does not freeze the hidden tab.
 */
export function UploadsProvider({ children }: { children: ReactNode }) {
  const { me } = useSession()
  const token = useSiteToken()
  const jobs = useMemo(() => {
    const chosen = Number(readStored(ENGINES_KEY))
    const size = chosen >= 1 && chosen <= maxEngines() ? chosen : defaultEngines()
    const created: UploadJobs = new UploadJobs(me.id, new EnginePool(size, () => new AnonymizationClient((stage) => created.stageChanged(stage))))
    return created
  }, [me.id])
  useEffect(() => () => jobs.close(), [jobs]) // logging out discards the engines and their token

  const normalized = token.state === 'set' ? normalizeToken(token.token) : null
  const { keyId, match, setLocked } = token
  // Annotations must fit the catalog as it stands (LBL-01), loaded once a token makes uploads possible; the
  // engines wait for it, and one that fails to load leaves them the engine's bundled list.
  const catalog = useApi<Label[]>(normalized && keyId && match ? '/labels' : null)
  const labels = catalog.data
  const ready = normalized && keyId && match && (labels !== undefined || catalog.error !== undefined)
  useEffect(() => {
    jobs.setToken(ready ? { setup: { token: normalized, site: match.site_config, labels }, keyId, siteId: match.site_id } : null)
  }, [jobs, ready, normalized, keyId, match, labels])

  const { jobs: views } = useSyncExternalStore(jobs.subscribe, jobs.snapshot)
  const working = views.some((job) => WORKING.has(job.phase))
  const waiting = working || views.some((job) => job.phase === 'queued')
  const leading = leadingJob(views)
  const percent = leading && WORKING.has(leading.phase) ? percentOf(leading) : undefined

  // Files are checked and sent with one token (TOK-07).
  useEffect(() => {
    setLocked(working)
    return () => setLocked(false)
  }, [working, setLocked])
  useLeaveWarning(waiting)
  useWebLock(waiting)
  useTitlePrefix(percent === undefined ? null : `(${percent} %)`)

  return <UploadsContext value={jobs}>{children}</UploadsContext>
}

export function useUploads(): UploadJobs {
  const jobs = useContext(UploadsContext)
  if (jobs === null) throw new Error('useUploads must be used inside UploadsProvider')
  return jobs
}

export function useUploadsState(): UploadsState {
  const jobs = useUploads()
  return useSyncExternalStore(jobs.subscribe, jobs.snapshot)
}

/** Changes how many engines work at once, and keeps the choice in this browser. */
export function chooseEngines(jobs: UploadJobs, count: number): void {
  jobs.resizeEngines(count)
  writeStored(ENGINES_KEY, String(count))
}

/** How far a job's current phase has come, in whole percent. */
export function percentOf(job: Parameters<typeof progressOf>[0]): number {
  const { done, of } = progressOf(job)
  return of > 0 ? Math.floor((done / of) * 100) : 0
}

/** The browser asks before the tab closes, which would pause the upload; not once the session has ended, which paused it already. */
function useLeaveWarning(active: boolean): void {
  useEffect(() => {
    if (!active) return
    const warn = (event: BeforeUnloadEvent) => {
      if (!sessionEnded()) event.preventDefault()
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [active])
}

function useWebLock(active: boolean): void {
  useEffect(() => {
    if (!active || !navigator.locks) return
    let release = () => {}
    const held = new Promise<void>((resolve) => (release = resolve))
    // A name of its own, so tabs never wait for each other's lock.
    void navigator.locks.request(`pedfx-upload-${crypto.randomUUID()}`, () => held)
    return release
  }, [active])
}

function useTitlePrefix(prefix: string | null): void {
  useEffect(() => {
    if (prefix === null) return
    const title = document.title
    document.title = `${prefix} ${title}`
    return () => {
      document.title = title
    }
  }, [prefix])
}
